/**
 * KhiCrossTwinCard — бъбречният риск в Cross-Twin графа.
 *
 * Показва активните физиологични връзки между бъбречния близнак и
 * метаболитния / чернодробния / кардио / лабораторния / възпалителния
 * близнак (GET /api/khi/cross-twin).
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Network, AlertTriangle, Activity, Info, Loader2 } from "lucide-react";

const SEV = {
  action: { Icon: AlertTriangle, label: "ДЕЙСТВИЕ", color: "#EF4444" },
  watch: { Icon: Activity, label: "НАБЛЮДЕНИЕ", color: "#F59E0B" },
  info: { Icon: Info, label: "ИНФО", color: "#0EA5E9" },
};

const LABELS = {
  kidney: "Бъбреци (KHI)", metabolic_insulin: "Метаболитен", liver_kidney: "Чернодробен",
  cardio: "Кардио", lab: "Лабораторен", inflammation: "Възпаление",
};

export default function KhiCrossTwinCard() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/khi/cross-twin`, { timeout: 30000 });
        if (alive) setData(data);
      } catch { /* silent */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [API]);

  const card = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";
  const sub = isDark ? "text-slate-400" : "text-slate-600";

  if (loading) {
    return (
      <div className={`p-4 rounded-2xl border ${card}`} data-testid="khi-crosstwin-loading">
        <Loader2 className="w-5 h-5 animate-spin text-cyan-500" />
      </div>
    );
  }
  if (!data) return null;

  const linked = data.linked_scores || {};

  return (
    <div className={`p-4 rounded-2xl border ${card}`} data-testid="khi-crosstwin-card">
      <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
        <div className="flex items-center gap-2">
          <Network className="w-4 h-4 text-fuchsia-500" />
          <h2 className="text-sm font-bold">Свързани органи: бъбреци ↔ метаболизъм ↔ черен дроб</h2>
        </div>
        <button onClick={() => navigate("/dashboard")} className="text-[10px] font-semibold text-fuchsia-500 hover:underline"
          data-testid="khi-crosstwin-open-graph">
          Целият граф →
        </button>
      </div>
      <div className="mb-2">
        <button onClick={() => navigate("/dashboard/liver-kidney-twin")}
          className="text-[10px] font-semibold text-emerald-500 hover:underline"
          data-testid="khi-crosstwin-open-liver">
          Виж чернодробно-бъбречния близнак (общи изследвания) →
        </button>
      </div>
      <p className={`text-[11px] mb-3 ${sub}`}>
        Бъбречният риск е свързан с другите близнаци — {data.active_count} активни връзки от {data.kidney_rules_total} правила.
      </p>

      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-3" data-testid="khi-crosstwin-scores">
        {["kidney", "metabolic_insulin", "liver_kidney", "cardio", "lab", "inflammation"].map((k) => {
          const v = k === "kidney" ? data.kidney_score : linked[k];
          return (
            <div key={k} className={`p-2 rounded-xl text-center ${isDark ? "bg-white/[0.03]" : "bg-slate-50"}`}
              data-testid={`khi-crosstwin-score-${k}`}>
              <div className="text-[9px] uppercase opacity-55 truncate">{LABELS[k]}</div>
              <div className={`text-sm font-bold ${k === "kidney" ? "text-cyan-500" : "text-fuchsia-500"}`}>{v ?? "—"}</div>
            </div>
          );
        })}
      </div>

      {data.active_count === 0 ? (
        <div className="p-2.5 rounded-xl bg-emerald-500/10 text-emerald-500 text-[11px]" data-testid="khi-crosstwin-empty">
          {data.kidney_score == null
            ? "Добавете eGFR / ренален панел, за да включим бъбречния близнак в графа."
            : "Няма активни бъбречно-системни връзки в момента."}
        </div>
      ) : (
        <div className="space-y-2" data-testid="khi-crosstwin-active">
          {data.active.map((a) => {
            const s = SEV[a.severity] || SEV.info;
            return (
              <div key={a.id} className={`p-2.5 rounded-xl border ${isDark ? "border-white/10 bg-white/[0.03]" : "border-gray-200 bg-slate-50"}`}
                data-testid={`khi-crosstwin-insight-${a.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="text-[12px] font-bold">{a.title_bg}</span>
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0"
                    style={{ background: s.color + "22", color: s.color }}>
                    {s.label}
                  </span>
                </div>
                <div className="text-[10px] opacity-60 mt-0.5">
                  {a.twins.map((t) => LABELS[t] || t).join(" ↔ ")} · {a.category_bg}
                </div>
                <div className="text-[11px] opacity-80 mt-1">{a.message_bg_ai || a.message_bg}</div>
                <div className="text-[10px] opacity-70 mt-1">→ {a.action_bg}</div>
                <div className="text-[9px] opacity-50 mt-1">{a.evidence_bg}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
